/**
 * Small pieces every landing section shares: the breathing orb, status chips,
 * the replay button, hash links that scroll smoothly, the trial link with its
 * GA4 event, the 25 point rank grid and a few inline icons.
 */

import { createContext, useContext, useEffect, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { trackGoogleEvent } from '@/utils/tracking';
import { reducedMotion } from './motion';
import { campaignSearch } from './variants/campaign';

export const START_PATH = '/gbp-growth-agent/start';
/**
 * The owner's app (Anand, 26 Sep 26: link /app from the landing page).
 * Signed out, /app sends the visitor to the start page's own sign-in with
 * return_to=/app (pages/app/useAppAccount.ts), so sign-in still happens on
 * the start page, never the Fly app's /login (Anand, 21 Sep 26), and lands
 * on the owner's locations afterwards instead of the set-up flow.
 */
export const APP_PATH = '/app';

/**
 * Whether this browser holds a Fly session: the same stored-token check the
 * app's AuthProvider makes, so the landing needs no Supabase client. Read
 * after mount, so the server-rendered page and the first paint agree.
 */
export function useHasSession(): boolean {
  const [has, setHas] = useState(false);
  useEffect(() => {
    try {
      setHas(Object.keys(window.localStorage).some((k) => k.startsWith('sb-') && k.endsWith('-auth-token')));
    } catch {
      // Storage blocked (private window): the link still works, it just says Sign in.
    }
  }, []);
  return has;
}

/** The link to the owner's app: Sign in when signed out, Your locations when signed in. */
export function AppLink({ placement, className }: { placement: string; className?: string }) {
  const signedIn = useHasSession();
  return (
    <Link
      to={APP_PATH}
      className={className}
      onClick={() => trackGoogleEvent(signedIn ? 'gbp_agent_app_click' : 'gbp_agent_login_click', { placement })}
    >
      {signedIn ? 'Your locations' : 'Sign in'}
    </Link>
  );
}

/**
 * The A/B variant a page belongs to, or null on the main landing. Every
 * TrialLink under a provider carries ?variant=<name> (plus any utm_* on the
 * page) to the start flow, so the campaign can be read in analytics.
 */
export const VariantContext = createContext<string | null>(null);

/* ------------------------------------------------------------------ orb */

export function Orb({ className = '' }: { className?: string }) {
  return (
    <span className={`ag-orb ${className}`.trim()} aria-hidden="true">
      <i />
    </span>
  );
}

/* ----------------------------------------------------------------- chip */

export type ChipTone = 'updated' | 'pending' | 'notlive' | 'missing';

export function Chip({ tone, className = '', children }: { tone: ChipTone; className?: string; children: ReactNode }) {
  return <span className={`ag-chip is-${tone} ${className}`.trim()}>{children}</span>;
}

/* --------------------------------------------------------------- replay */

export function ReplayButton({ onClick }: { onClick: () => void }) {
  return (
    <button className="replay" type="button" aria-label="Replay" onClick={onClick}>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 12a8 8 0 1 0 2.3-5.7" />
        <path d="M4 5v4.5h4.5" />
      </svg>
    </button>
  );
}

/* ------------------------------------------------------------ hash link */

/** Scrolls to #id on the page (smooth unless motion is reduced) and records the hash. */
export function HashLink({ to, className, children, onClick }: { to: string; className?: string; children: ReactNode; onClick?: () => void }) {
  const go = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.();
    const id = to.replace(/^#/, '');
    const target = id === 'top' ? document.documentElement : document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    if (id === 'top') window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
    else target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    window.history.replaceState(null, '', id === 'top' ? window.location.pathname + window.location.search : `#${id}`);
  };
  return (
    <a href={to} className={className} onClick={go}>
      {children}
    </a>
  );
}

/* ----------------------------------------------------------- trial link */

export function trackCta(placement: string, extra: Record<string, unknown> = {}) {
  trackGoogleEvent('gbp_agent_cta_click', { placement, ...extra });
}

export function TrialLink({ placement, className, children, extra }: { placement: string; className: string; children: ReactNode; extra?: Record<string, unknown> }) {
  const variant = useContext(VariantContext);
  const { search } = useLocation();
  const to = variant ? `${START_PATH}${campaignSearch(search, variant)}` : START_PATH;
  return (
    <Link to={to} className={className} onClick={() => trackCta(placement, variant ? { variant, ...extra } : extra)}>
      {children}
    </Link>
  );
}

/* ------------------------------------------------------------ rank grid */

/** 25 points, ripple from the shop outward: r1 filled, r2 ring, r3 faint ring, r4 grey. */
const GRID = [1, 1, 1, 1, 2, 1, 1, 1, 1, 2, 1, 1, 1, 1, 1, 2, 1, 1, 1, 1, 3, 2, 1, 1, 4];
/** The points that already had a reading before the pass. */
const PRE = new Set([6, 7, 11, 12, 13, 17]);

export function RankGrid({ className = '', label }: { className?: string; label?: string }) {
  return (
    <span className={`rgrid ${className}`.trim()} aria-hidden={label ? undefined : true} aria-label={label}>
      {GRID.map((r, i) => {
        const x = i % 5;
        const y = Math.floor(i / 5);
        const k = Math.max(Math.abs(x - 2), Math.abs(y - 2));
        return <i key={i} className={`r${r}`} style={{ '--k': k } as CSSProperties} data-pre={PRE.has(i) ? '' : undefined} />;
      })}
    </span>
  );
}

/* ---------------------------------------------------------------- icons */

export const TickPath = () => <path pathLength={1} d="M5 12.5l4.5 4.5L19 7" />;

export function Tick() {
  return (
    <span className="tick">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <TickPath />
      </svg>
    </span>
  );
}

export const IconPhone = () => <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />;
export const IconChat = () => <path d="M4 5h16v11H8l-4 4z" />;
export const IconStranger = () => (
  <>
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5 20a7 7 0 0 1 14 0" />
  </>
);
